import React, { forwardRef, useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Container,
  CircularProgress,
  Grid,
  Typography,
  TextField,
  IconButton,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Fade from "@mui/material/Fade";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

import StyledPaper from "../StyledPaper";
import { localDateTimeToUtc, utcDateTimeToLocal } from "../timeUtil";
import { ImageHandler } from "../ImageHandler";
import useDeedSuggestions from "../../../hooks/useDeedSuggestions";
import {
  useAddDeed,
  useUpdateDeed,
  useDeleteDeed,
} from "../../../hooks/useDeedMutations";
import {
  requestUploadUrl,
  uploadFileToSupabase,
  deleteImage,
} from "../../../services/api";
import useImageManager from "./useImageManager";
import updateAllDescriptions from "./updateAllDescriptions";
import useChangeAllDescriptionsDialog from "./useChangeAllDescriptionsDialog";

const DeedEdit = forwardRef(
  (
    { sortedDeeds, isEditMode, selectedDeedId, handleClose },
    ref
  ) => {
    const id = selectedDeedId;
    const addDeedMutation = useAddDeed();
    const updateDeedMutation = useUpdateDeed();
    const deleteDeedMutation = useDeleteDeed();

    const [loading, setLoading] = useState(false); // loading state

    const [deed, setDeed] = useState(null);
    const [dateTime, setDateTime] = useState("");
    const [description, setDescription] = useState("");
    const [bucket, setBucket] = useState("");
    const [notes, setNotes] = useState("");
    const [hours, setHours] = useState("");
    const [minutes, setMinutes] = useState("");

    const [initialDescription, setInitialDescription] = useState(""); // So Save Changes will work with description & bucket on blur
    const [initialBucket, setInitialBucket] = useState("");
    const [initialNotes, setInitialNotes] = useState("");

    const [lastUsedBucket, setLastUsedBucket] = useState(new Map()); // For autobucket

    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [exitConfirmationOpen, setExitConfirmationOpen] = useState(false); // for confirm Revert dialog

    // Image attachment management. Declared before initializeDeedData so its
    // setExistingPics setter is in scope (and explicitly tracked as a dep).
    const {
      newPics,
      existingPics,
      handleFileChange,
      handleDeletePic,
      cleanup,
      reset,
      setExistingPics,
    } = useImageManager(deed, setHasUnsavedChanges);

    // Populate form in Edit mode. Wrapped in useCallback so the identity is
    // stable across renders and we can safely depend on it from useEffect.
    const initializeDeedData = useCallback((deedData) => {
      setDeed(deedData);

      setDateTime(utcDateTimeToLocal(deedData.dateTime));
      setDescription(deedData.description);
      setBucket(deedData.bucket ?? "");
      setNotes(deedData.notes);
      setExistingPics(deedData.pics || []);
      setHours(Math.floor(deedData.timeSpent / 60));
      setMinutes(deedData.timeSpent % 60);

      setInitialDescription(deedData.description);
      setInitialBucket(deedData.bucket ?? "");
      setInitialNotes(deedData.notes);
    }, [setExistingPics]);

    useEffect(() => {
      if (isEditMode) {
        const deedData = sortedDeeds.find((d) => d.id === id);
        if (deedData) {
          initializeDeedData(deedData);
        } else {
          console.error("Deed not found:", id);
        }
      } else {
        setDeed("");
        setDateTime(utcDateTimeToLocal(new Date()));
        setHours("0");
        setMinutes("0");
        setNotes("");
        setBucket("");
      }
    }, [id, sortedDeeds, isEditMode, initializeDeedData]);

    // Reset form
    const resetChanges = () => {
      initializeDeedData(deed);
      setChangeAllDescriptionsDialogShown(false);
      reset();
      setChangeAllDescriptions(false);
      setHasUnsavedChanges(false);
      setExitConfirmationOpen(false);
    };

    // Image Cleanup — fire the cleanup whenever newPics changes (and on unmount).
    // We intentionally only depend on newPics here; cleanup's identity may change
    // every render but we only want this to re-run when newPics changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => cleanup, [newPics]);

    // START - auto bucket

    // Populate Autocomplete
    const { deedOptions, bucketOptions } = useDeedSuggestions();

    // Populate last bucket used with the deed
    useEffect(() => {
      const fetchDeeds = async () => {
        try {
          const fetchedDeeds = sortedDeeds;

          // Last bucket used with each deed
          const deedBucketMap = new Map();
          fetchedDeeds.forEach((deed) => {
            // If this is a new deed or has a more recent dateTime, update the deedBucketMap
            if (
              !deedBucketMap.has(deed.description) ||
              new Date(deed.dateTime) >
                new Date(deedBucketMap.get(deed.description).dateTime)
            ) {
              deedBucketMap.set(deed.description, {
                bucket: deed.bucket,
                dateTime: deed.dateTime,
              });
            }
          });
          setLastUsedBucket(deedBucketMap);
          return fetchedDeeds;
        } catch (error) {
          console.error("Error fetching deeds:", error);
        }
      };
      fetchDeeds();
    }, [sortedDeeds]);

    /*  called whenever the description value changes.
        If the lastUsedBucket map has the chosen description, it will set the bucket state to the last used bucket for that description.
        If the description is new, it will clear the bucket state. */
  const handleDescriptionChange = (newDescription) => {
    if (lastUsedBucket.has(newDescription)) {
      setBucket(lastUsedBucket.get(newDescription).bucket ?? "");
    } else {
      setBucket(""); // Clear the bucket if the description is new
    }
  };

  // Needed for edge case when user adds the same twice deed in a row
  useEffect(() => {
    if (lastUsedBucket.has(description) && !isEditMode) {
      setBucket(lastUsedBucket.get(description).bucket ?? "");
    }
  }, [description, lastUsedBucket, isEditMode]);
    // END - auto bucket

    // FORM SUBMISSION
    const handleSubmit = async (event) => {
      event.preventDefault();

      if (!description || !dateTime) {
        alert("Please fill out the required fields.");
        return;
      }

      const timeSpent = 60 * parseInt(hours) + parseInt(minutes);
      const utcDateTime = localDateTimeToUtc(dateTime);

      // Storage paths the user removed during this edit. Cleaned up from
      // Supabase Storage after the deed update succeeds.
      const removedPaths = isEditMode
        ? (deed?.pics || []).filter((p) => !existingPics.includes(p))
        : [];

      try {
        // 1. Upload any newly-attached files directly to Supabase Storage via
        //    signed upload URLs.
        const uploadedPaths = [];
        for (const file of newPics.values()) {
          const { data } = await requestUploadUrl({
            filename: file.name,
            contentType: file.type,
            deedId: isEditMode ? id : "new",
          });
          await uploadFileToSupabase(data.signedUrl, file);
          uploadedPaths.push(data.path);
        }

        // 2. Save the deed with the merged storage-path list.
        const deedJson = {
          dateTime: utcDateTime,
          description,
          bucket,
          notes,
          timeSpent,
          pics: [...existingPics, ...uploadedPaths],
        };

        if (isEditMode) {
          await updateDeedMutation.mutateAsync({ id, deedJson });
          if (changeAllDescriptions) {
            await updateAllDescriptions(
              sortedDeeds,
              initialDescription,
              description
            );
          }

          // 3. Best-effort cleanup of images dropped during the edit. We don't
          // block submission on these — the deed record is already updated.
          // Note: the API PUT also performs this cleanup, so this is belt &
          // suspenders for cases where the user re-attached an image first.
          if (removedPaths.length > 0) {
            await Promise.allSettled(
              removedPaths.map((path) => deleteImage(path))
            );
          }
        } else {
          await addDeedMutation.mutateAsync(deedJson);
        }
      } catch (error) {
        console.error("Error saving deed:", error);
      } finally {
        setHasUnsavedChanges(false);
        setLoading(false);
        setChangeAllDescriptions(false);
        handleClose();
      }
    };

    // For image handler
    const allPics = [...existingPics, ...newPics.keys()];

    // Change All Descriptions dialog management
    const {
      openChangeAllDescriptionsDialog,
      setOpenChangeAllDescriptionsDialog,
      changeAllDescriptions,
      setChangeAllDescriptions,
      ChangeAllDescriptionsDialogShown,
      setChangeAllDescriptionsDialogShown,
      countDescriptionInstances,
    } = useChangeAllDescriptionsDialog();

    const handleDelete = async (deedId) => {
      if (!window.confirm("Are you sure you want to delete this deed?")) return;
      try {
        await deleteDeedMutation.mutateAsync(deedId);
        handleClose();
      } catch (error) {
        console.error("Error deleting deed:", error);
      }
    };

    return (
      <Container
        maxWidth="xs"
        ref={ref}
        tabIndex={-1}
        style={{
          // to center on the page
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center", // <-- Vertically centers the container's children
          padding: "8px",
          overflowY: "auto", // Enale scroll for small screens
        }}
      >
        <StyledPaper sx={{ px: { xs: 2, sm: 4 } }}>
          <Box my={0}>
            {/* Close, Edit, Delete */}
            <Box
              display="flex"
              alignItems={"center"}
              justifyContent={"space-between"}
              mx={-1.5}
              mb={1}
            >
              <Box>
                {isEditMode ? (
                  <IconButton onClick={() => handleDelete(deed.id)}>
                    <DeleteRoundedIcon color="error" />
                  </IconButton>
                ) : (
                  ""
                )}
              </Box>
              <Typography>{isEditMode ? "Edit" : "New"}</Typography>

              <IconButton onClick={handleClose}>
                <CloseRoundedIcon />
              </IconButton>
            </Box>

            {!isEditMode || deed ? (
              <form
                onSubmit={handleSubmit}
                noValidate
                autoComplete="off"
              >
                <Box mb={2}>
                  <TextField
                    variant="outlined"
                    fullWidth
                    size="small"
                    type="datetime-local"
                    label=""
                    value={dateTime}
                    onChange={(e) => {
                      setDateTime(e.target.value);
                      setHasUnsavedChanges(true);
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    inputProps={{
                      style: { textAlign: "center" },
                    }}
                    required
                  />
                </Box>
                <Box mb={2}>
                  <Autocomplete
                    variant="outlined"
                    size="small"
                    selectOnFocus
                    clearOnBlur
                    id="description-autocomplete"
                    options={deedOptions}
                    freeSolo
                    value={description}
                    onInputChange={(event, newInputValue) => {
                      setDescription(newInputValue);
                      if (newInputValue !== initialDescription) {
                        setHasUnsavedChanges(true); // Detect changes here
                      }
                    }}
                    onChange={(event, newInputValue) => {
                      handleDescriptionChange(newInputValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        label="I just..."
                        required
                        onKeyDown={(event) => {
                          // Check if 'Enter' key is pressed and description is changed
                          if (
                            event.key === "Enter" && // prevents 'Enter' keypress from bypassing the Change All dialog
                            isEditMode &&
                            description !== initialDescription &&
                            countDescriptionInstances(
                              sortedDeeds,
                              initialDescription
                            ) > 1 && // when more than 1 instance will be affected
                            !ChangeAllDescriptionsDialogShown
                          ) {
                            // Prevent form submission
                            event.preventDefault();

                            // Show the dialog
                            setOpenChangeAllDescriptionsDialog(true);
                            setChangeAllDescriptionsDialogShown(true);
                          }
                        }}
                        onBlur={(event) => {
                          if (
                            isEditMode &&
                            description !== initialDescription &&
                            countDescriptionInstances(
                              sortedDeeds,
                              initialDescription
                            ) > 1 && // when more than 1 instance will be affected
                            !ChangeAllDescriptionsDialogShown
                          ) {
                            // Check if description is changed and Dialog has not been shown
                            setOpenChangeAllDescriptionsDialog(true); // Open the Dialog
                            setChangeAllDescriptionsDialogShown(true); // Set the flag to true
                          }
                        }}
                      />
                    )}
                  />
                  <Dialog
                    open={openChangeAllDescriptionsDialog}
                    onClose={() => setOpenChangeAllDescriptionsDialog(false)}
                  >
                    <DialogTitle>Rename All</DialogTitle>
                    <DialogContent>
                      <DialogContentText>
                        Rename{" "}
                        <span style={{ fontWeight: "bold" }}>
                          {countDescriptionInstances(
                            sortedDeeds,
                            initialDescription
                          )}
                        </span>{" "}
                        instances of{" "}
                        <span style={{ fontWeight: "bold" }}>
                          {initialDescription}
                        </span>{" "}
                        to{" "}
                        <span style={{ fontWeight: "bold" }}>
                          {description}
                        </span>
                        ?
                      </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                      <Button
                        onClick={() => {
                          setChangeAllDescriptions(true);
                          setOpenChangeAllDescriptionsDialog(false);
                        }}
                      >
                        Update All
                      </Button>
                      <Button
                        onClick={() => {
                          setChangeAllDescriptions(false);
                          setOpenChangeAllDescriptionsDialog(false);
                        }}
                      >
                        Just this one
                      </Button>
                    </DialogActions>
                  </Dialog>
                </Box>
                <Box mb={2}>
                  <Autocomplete
                    variant="outlined"
                    size="small"
                    selectOnFocus
                    clearOnBlur
                    id="bucket-autocomplete"
                    freeSolo
                    options={bucketOptions}
                    getOptionLabel={(option) => option}
                    value={bucket}
                    onInputChange={(event, newInputValue) => {
                      setBucket(newInputValue);
                      if (newInputValue !== initialBucket) {
                        setHasUnsavedChanges(true); // Detect changes here
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Bucket"
                        variant="outlined"
                      />
                    )}
                  />
                </Box>
                <Box mb={2}>
                  <TextField
                    variant="outlined"
                    fullWidth
                    multiline
                    rows={5}
                    label="Notes"
                    value={notes}
                    onChange={(e) => {
                      setNotes(e.target.value);
                      if (e.target.value !== initialNotes) {
                        setHasUnsavedChanges(true);
                      }
                    }}
                  />
                </Box>
                <Box>
                  <ImageHandler
                    allPics={allPics}
                    handleDeletePic={handleDeletePic}
                    handleFileChange={handleFileChange}
                  />
                </Box>

                <Grid
                  container
                  justifyContent="center"
                  alignItems="center"
                  spacing={0}
                >
                  <Grid item xs={6} pr={1}>
                    <TextField
                      variant="outlined"
                      size="small"
                      margin="normal"
                      fullWidth
                      type="number"
                      label="hours"
                      value={hours}
                      onChange={(e) => {
                        setHours(e.target.value);
                        setHasUnsavedChanges(true);
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Grid>
                  <Grid item xs={6} pl={1}>
                    <TextField
                      variant="outlined"
                      margin="normal"
                      size="small"
                      fullWidth
                      type="number"
                      label="minutes"
                      value={minutes}
                      onChange={(e) => {
                        setMinutes(e.target.value);
                        setHasUnsavedChanges(true);
                      }}
                    />
                  </Grid>
                </Grid>

                {loading ? (
                  <CircularProgress />
                ) : !hasUnsavedChanges ? (
                  <Grid
                    container
                    spacing={2}
                    mt={0}
                    mb={0}
                    justifyContent={"space-around"}
                  >
                    <Grid item xs={12}>
                      <Button
                        fullWidth
                        variant="outlined"
                        color="primary"
                        onClick={handleClose}
                      >
                        Close
                      </Button>
                    </Grid>
                  </Grid>
                ) : (
                  hasUnsavedChanges && (
                    <Fade in={hasUnsavedChanges}>
                      <Grid container spacing={2} mt={0} mb={0}>
                        {isEditMode && (
                          <Grid item xs={6}>
                            <Button
                              fullWidth
                              variant="outlined"
                              color="primary"
                              onClick={() => setExitConfirmationOpen(true)}
                            >
                              Revert
                            </Button>
                          </Grid>
                        )}
                        <Grid item xs={isEditMode ? 6 : 12}>
                          <Button
                            fullWidth
                            type="submit"
                            variant="contained"
                            color="primary"
                          >
                            Done
                          </Button>
                        </Grid>
                        {changeAllDescriptions && (
                          <Grid item xs={12}>
                            <Typography variant="caption" color="#ed6c02">
                              <span style={{ fontWeight: "bold" }}>
                                {countDescriptionInstances(
                                  sortedDeeds,
                                  initialDescription
                                )}
                              </span>{" "}
                              instances of{" "}
                              <span style={{ fontWeight: "bold" }}>
                                {initialDescription}
                              </span>{" "}
                              will be renamed{" "}
                              <span style={{ fontWeight: "bold" }}>
                                {description}
                              </span>
                              .
                            </Typography>
                          </Grid>
                        )}
                      </Grid>
                    </Fade>
                  )
                )}
              </form>
            ) : (
              <Fade in={loading}>
                <CircularProgress />
              </Fade>
            )}
          </Box>
        </StyledPaper>
        {/* Exit confirmation dialog */}
        <Dialog
          open={exitConfirmationOpen}
          onClose={() => setExitConfirmationOpen(false)}
        >
          <DialogTitle>{"Revert Changes"}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to revert your unsaved changes?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setExitConfirmationOpen(false)}>No</Button>
            <Button onClick={resetChanges} autoFocus>
              Yes
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    );
  }
);

export default DeedEdit;
